import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { navLinkProps } from "../controllers/appController";
import Logo from "./Logo";
import useServiceContext from "../hooks/useServiceContext";
import { paths } from "../models/paths";
import "./Navbar.css";

// Header menu (content pack section 8, S17). The "Areas" item is left out
// while the suburb pages are on hold (C14). Dropdown links stay in the DOM
// so crawlers can follow them.
const menu = [
  {
    label: "House Cleaning",
    path: paths.houseHub,
    children: [
      { label: "Regular Cleaning", path: paths.regular },
      { label: "Deep Cleaning", path: paths.deep },
      { label: "End of Tenancy", path: paths.endOfTenancy },
      { label: "Oven & Carpet", path: paths.ovenCarpet },
      { label: "Prices", path: paths.prices },
    ],
  },
  {
    label: "Commercial",
    path: paths.commercial,
    children: [
      { label: "Office Cleaning", path: paths.office },
      { label: "School Cleaning", path: paths.school },
      { label: "Projects", path: paths.projects },
    ],
  },
  { label: "Blog", path: paths.blog },
  { label: "About", path: paths.about },
  { label: "Contact", path: paths.contact },
];

const Navbar = ({ dark = false }) => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const isDomestic = useServiceContext() === "domestic";
  const ctaPath = isDomestic ? paths.inspection : paths.contact;

  const isLinkActive = (link) => {
    const currentPath = location.pathname.replace(/\/$/, "");
    return currentPath === link.path || (link.children || []).some((child) => child.path === currentPath);
  };

  // Blur after a click so a :focus-within dropdown closes once the page changes.
  const menuLinkProps = (path, close) => {
    const props = navLinkProps(navigate, path, close);
    return {
      ...props,
      onClick: (event) => {
        props.onClick(event);
        event.currentTarget.blur();
      },
    };
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  useEffect(() => {
    // Only record the resting (unscrolled, closed) height: re-measuring the
    // shrunken scrolled navbar moved every hero while the user scrolled.
    const updateNavbarHeight = () => {
      const navElement = document.querySelector(".navbar");
      if (navElement && !scrolled && !menuOpen) {
        document.documentElement.style.setProperty(
          "--navbar-height",
          `${navElement.offsetHeight}px`
        );
      }
    };

    updateNavbarHeight();

    window.addEventListener("resize", updateNavbarHeight);
    const timeoutId = setTimeout(updateNavbarHeight, 100);

    return () => {
      window.removeEventListener("resize", updateNavbarHeight);
      clearTimeout(timeoutId);
    };
  }, [scrolled, menuOpen, location.pathname]);

  return (
    <>
      <nav className={`navbar ${scrolled ? "navbar--scrolled" : ""} ${dark ? "navbar--dark" : ""} ${menuOpen ? "navbar--open" : ""}`}>
        <div className="navbar__inner">
          {/* Logo — Universal return to global ServiceSelectorPage */}
          <a
            className="navbar__logo"
            aria-label="PureNest Cleaning home"
            {...navLinkProps(navigate, "/", null)}
          >
            <Logo light={!scrolled && !dark && !menuOpen} height={44} />
          </a>

          {/* Desktop Links */}
          <div className="navbar__center">
            <ul className="navbar__links">
              {menu.map((link) => (
                <li key={link.path} className={link.children ? "navbar__item navbar__item--has-menu" : "navbar__item"}>
                  <a
                    className={`navbar__link ${link.children ? "navbar__link--parent" : ""} ${isLinkActive(link) ? "active" : ""}`}
                    {...menuLinkProps(link.path, null)}
                  >
                    {link.label}
                  </a>
                  {link.children && (
                    <ul className="navbar__dropdown" aria-label={`${link.label} pages`}>
                      {link.children.map((child) => (
                        <li key={child.path}>
                          <a
                            className={`navbar__dropdown-link ${location.pathname === child.path ? "active" : ""}`}
                            {...menuLinkProps(child.path, null)}
                          >
                            {child.label}
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* CTA */}
          <a className="navbar__cta" {...menuLinkProps(ctaPath, null)}>
            Free Inspection →
          </a>

          {/* Hamburger */}
          <button
            className={`navbar__hamburger ${menuOpen ? "open" : ""}`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
            aria-expanded={menuOpen}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </nav>

      {/* Mobile Overlay */}
      <div
        className={`navbar__overlay ${menuOpen ? "navbar__overlay--visible" : ""}`}
        onClick={() => setMenuOpen(false)}
      />

      {/* Mobile Menu */}
      <div className={`navbar__mobile ${menuOpen ? "navbar__mobile--open" : ""}`}>
        {menu.map((link) => (
          <React.Fragment key={link.path}>
            <a
              className={`navbar__mobile-link ${isLinkActive(link) ? "active" : ""}`}
              {...navLinkProps(navigate, link.path, setMenuOpen)}
            >
              {link.label}
            </a>
            {link.children && (
              <div className="navbar__mobile-sublinks">
                {link.children.map((child) => (
                  <a
                    key={child.path}
                    className={`navbar__mobile-sublink ${location.pathname === child.path ? "active" : ""}`}
                    {...navLinkProps(navigate, child.path, setMenuOpen)}
                  >
                    {child.label}
                  </a>
                ))}
              </div>
            )}
          </React.Fragment>
        ))}
        <a className="navbar__mobile-cta" {...navLinkProps(navigate, ctaPath, setMenuOpen)}>
          Free Inspection →
        </a>
      </div>
    </>
  );
};

export default Navbar;
